// Nearby list: the closest sites first (or A-Z without a location), with search and category chips.
// Built once; update() only replaces the header text and the rows, so the search box keeps focus.
import { h, icon } from './dom.js';
import { ICONS } from './icons.js';
import { formatDistance } from '../lib/geo.js';

/** handlers: { onSelect(slug), onQuery(q), onToggleCategory(slug), onLocate() } */
export function createNearby(categories, handlers) {
  const title = h('h2', {});
  const sub = h('p', { class: 'meta' });
  const locateBtn = h('button', { type: 'button', class: 'pill', onclick: handlers.onLocate });
  const search = h('input', {
    type: 'search', class: 'search', placeholder: 'Search by name or street', 'aria-label': 'Search sites',
    enterkeyhint: 'search', autocomplete: 'off',
  });
  search.addEventListener('input', () => handlers.onQuery(search.value));
  const chips = categories.map((c) => h('button', {
    type: 'button', class: 'chip-toggle', 'aria-pressed': 'false', dataset: { slug: c.slug },
    style: { '--chip': c.color }, onclick: () => handlers.onToggleCategory(c.slug),
  }, c.label));
  const list = h('ul', { class: 'site-list', role: 'list' });
  const empty = h('p', { class: 'empty', hidden: true }, 'No sites match. Clear the search or filters to see all sites.');
  const catBy = new Map(categories.map((c) => [c.slug, c]));

  const el = h('div', { class: 'nearby' },
    h('div', { class: 'nearby-head' }, h('div', {}, title, sub), locateBtn),
    h('div', { class: 'list-tools' },
      h('label', { class: 'search-wrap' }, icon(ICONS.search), search),
      h('div', { class: 'chips', role: 'group', 'aria-label': 'Filter by category' }, chips)),
    list,
    empty);

  /** state: { sites (sorted, with distance), total, active (Set), haveLocation, locating, visited (Set) } */
  function update(state) {
    title.textContent = state.haveLocation ? 'Near you' : 'Historic sites';
    sub.textContent = `${state.sites.length} of ${state.total} sites${state.haveLocation ? ', closest first' : ''}`;
    locateBtn.hidden = state.haveLocation;
    locateBtn.textContent = state.locating ? 'Finding you…' : 'Use my location';
    for (const c of chips) c.setAttribute('aria-pressed', String(state.active.has(c.dataset.slug)));
    empty.hidden = state.sites.length > 0;
    list.replaceChildren(...state.sites.map((s) => {
      const cat = catBy.get(s.category);
      return h('li', {}, h('button', { type: 'button', class: 'site-row', onclick: () => handlers.onSelect(s.slug) },
        h('span', { class: 'dot', style: { background: cat?.color ?? '#6b5a43' }, 'aria-hidden': 'true' }),
        h('span', { class: 'row-text' },
          h('span', { class: 'row-name' }, s.name),
          h('span', { class: 'row-sub' }, [s.distance != null && formatDistance(s.distance), s.address.split(',')[0]].filter(Boolean).join(' · '))),
        state.visited.has(s.slug) && icon(ICONS.check, 'Visited')));
    }));
  }

  return { el, update };
}
